/* eslint-disable react/no-unstable-nested-components */
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Input, Tag, Select } from "@jhool-io/fe-components";
import { useParams } from "react-router-dom";
import { IAddClientFlag } from "../../types/clients.types";
import useToast from "../../../../hooks/useToast";
import { useUpdateClientFlag } from "../../hooks/clients.mutations";
import { useFetchClientFlags } from "../../hooks/clients.queries";
import { UserRole } from "../../../../utils/types/user";
import { useDoesUserHaveRole } from "../../../../hooks/permissions";
import {
    DEFAULT_FLAG_COLOR,
    createFlagContent,
    FLAG_CONTENT_DELIMITER,
    getClientFlagDetails,
    FLAG_COLORS,
} from "../../helpers/clients.helpers";

interface EditClientFlagFormProps {
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
    // Flag Id
    flagId: string;
}

interface ColorOption {
    value: string;
    label: string;
}

// For form validation purposes
const schema = yup.object({
    content: yup
        .string()
        .required("Flag label is required")
        .max(35, "Maximum number of characters is exceeded")
        .test(
            "no-delimiter",
            `Flag label cannot contain the "${FLAG_CONTENT_DELIMITER}" symbol`,
            (value) => !value?.includes(FLAG_CONTENT_DELIMITER)
        ),
    color: yup.string().required("Color is required"),
});

const colorOptions: ColorOption[] = FLAG_COLORS.map((color) => ({
    value: color.value,
    label: color.name,
}));

function ColorOptionComponent({ option }: { option: ColorOption }) {
    return (
        <div className="flex items-center gap-3">
            <div
                className="size-4 rounded-md"
                style={{ backgroundColor: option.value }}
            />
            <span className="font-medium">{option.label}</span>
        </div>
    );
}

export default function EditClientFlagForm({
    onFormSubmit,
    flagId,
}: EditClientFlagFormProps) {
    const params = useParams();

    const clientId = params.clientId as string;

    const userCanViewFlags = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
        UserRole.CODER,
    ]);

    // Fetch client flags
    const clientFlags = useFetchClientFlags(clientId, userCanViewFlags);

    const selectedFlagDetails = clientFlags.data?.data.find(
        (flag) => flag.client_flag_id === flagId
    );

    const parsedFlagDetails = selectedFlagDetails
        ? getClientFlagDetails(selectedFlagDetails.content)
        : { label: "", color: DEFAULT_FLAG_COLOR };

    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useForm<IAddClientFlag>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            content: parsedFlagDetails ? parsedFlagDetails.label : "",
            color: parsedFlagDetails
                ? parsedFlagDetails.color
                : DEFAULT_FLAG_COLOR,
        },
    });

    const watchedColor = watch("color");
    const watchedContent = watch("content");

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const editFlag = useUpdateClientFlag(clientId, flagId);

    const onSubmit = (data: IAddClientFlag) => {
        const flagData = {
            content: createFlagContent(data.content, data.color),
            color: data.color,
        };
        editFlag.mutate(flagData, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "client-flags"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Flag edited successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not edit flag at this time.",
                });
            },
        });
    };

    return (
        <form id="edit-client-flag" onSubmit={handleSubmit(onSubmit)}>
            <Alert
                classNames="fg"
                type="info"
                description="A Client Flag is a customizable label that highlights notes or reminders linked to a client."
            />

            <div className="fg">
                <Input
                    {...register("content")}
                    label="Flag label"
                    placeholder="Flag label"
                    hasError={!!errors.content}
                    errorText={errors.content?.message}
                />
            </div>

            <Controller
                name="color"
                control={control}
                render={({ field }) => (
                    <Select
                        label="Flag color"
                        value={colorOptions.find(
                            (option) => option.value === field.value
                        )}
                        options={colorOptions}
                        onChange={(newValue) => {
                            const option = newValue as ColorOption | null;
                            if (option) {
                                field.onChange(option.value);
                            }
                        }}
                        placeholder="Select a color"
                        isLongListInDialog
                        getOptionLabel={(options: unknown) => {
                            const option = options as ColorOption;
                            return option.label;
                        }}
                        formatOptionLabel={(data: unknown) => {
                            const option = data as ColorOption;
                            return <ColorOptionComponent option={option} />;
                        }}
                    />
                )}
            />
            <div className="mt-[--spacing-items]">
                <Tag
                    title={watchedContent || "Flag label"}
                    bgColor={watchedColor}
                    textColor="#ffffff"
                />
            </div>
        </form>
    );
}
